import { z } from 'zod';

const MIN_PASSWORD_LENGTH = 8;
const MAX_PASSWORD_LENGTH = 256;

export const emailValidationSchema = z.string().trim().email('Email is not valid.');

export const minLengthSchema = z
  .string()
  .min(MIN_PASSWORD_LENGTH, { message: 'Password must be at least 8 characters long.' });

export const maxLengthSchema = z
  .string()
  .max(MAX_PASSWORD_LENGTH, { message: 'Password must be maximum least 256 characters long.' });

export const letterSchema = z.string().regex(/[a-zA-Z]/, { message: 'Password must contain at least one letter.' });

export const digitSchema = z.string().regex(/\d/, { message: 'Password must contain at least one digit.' });

export const specialCharSchema = z.string().regex(/[=+\-^$*.[\]{}()?"!@#%&/,><\\':;|_~`]+/, {
  message: 'Password must contain at least one special character.'
});

export const noWhitespaceSchema = z
  .string()
  .regex(/^\S+$/, { message: 'Password must not start or end with whitespace.' });

export const passwordSchema = minLengthSchema
  .and(maxLengthSchema)
  .and(letterSchema)
  .and(digitSchema)
  .and(specialCharSchema)
  .and(noWhitespaceSchema);

export const ApplicantNewPasswordSchema = z.object({
  clientId: z.string(),
  email: z.string().and(emailValidationSchema),
  newPassword: z.string().min(MIN_PASSWORD_LENGTH),
  session: z.string()
});

export const ApplicantRegistrationSchema = z.object({
  knownBy: z.string().trim(),
  email: z.string().and(emailValidationSchema),
  marketing: z.boolean(),
  referrer: z.string()
});

export const ApplicantLoginSchema = z.object({
  email: z.string().and(emailValidationSchema),
  password: z.string().and(passwordSchema)
});

export const ApplicantChangePasswordSchema = ApplicantLoginSchema.extend({
  code: z.string().optional()
});

export const StudentAuthoriseAgentSchema = z.object({
  studentAuthoriseAgent: z.coerce.boolean(),
  marketingConsent: z.boolean().optional()
});

// Define the schema for agent registration
export const AgentRegistrationSchema = z.object({
  email: z.string().email(),
  preferredName: z.string().min(1),
  universityId: z.string().min(2),
  intakeId: z.string().min(1),
  studyLevel: z.string().min(1),
  feeStatus: z.string().min(1),
  courseId: z.string().min(1),
  nationality: z.string().min(3),
  dob: z.string().min(10),
  phoneNumber: z.string().min(7),
  agentConsent: z.boolean()
});

/* eslint-disable @typescript-eslint/no-empty-object-type */
export interface IApplicantRegistration extends z.infer<typeof ApplicantRegistrationSchema> {}

export interface IApplicantLogin extends z.infer<typeof ApplicantLoginSchema> {}

export interface IApplicantNewPassword extends z.infer<typeof ApplicantNewPasswordSchema> {}

export interface IApplicantChangePassword extends z.infer<typeof ApplicantChangePasswordSchema> {}

export interface IStudentAuthoriseAgentSchema extends z.infer<typeof StudentAuthoriseAgentSchema> {}

export interface IAgentRegistration extends z.infer<typeof AgentRegistrationSchema> {}
